import { useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Target, Trophy, Repeat, Loader2, Crown } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export const TipGoalBar = ({ goal, name, testid = "tip-goal-bar", compact = false }) => {
  if (!goal) return null;
  const pct = Math.min(100, Math.round((goal.progress / goal.target) * 100));
  return (
    <div data-testid={testid} className={`rounded-2xl border border-[#D4AF37]/40 bg-black/60 backdrop-blur-md ${compact ? "px-3 py-2" : "p-4"}`}>
      <div className="flex items-center justify-between gap-3 text-xs mb-1.5">
        <span className="flex items-center gap-1.5 text-white min-w-0">
          {goal.completed ? <Trophy size={13} className="text-[#D4AF37] flex-shrink-0" /> : <Target size={13} className="text-[#D4AF37] flex-shrink-0" />}
          <span className="truncate">{name ? <span className="text-[#A0A0A0]">{name}: </span> : null}{goal.title}</span>
        </span>
        <span className="text-[#D4AF37] font-medium whitespace-nowrap" data-testid={`${testid}-progress`}>
          {goal.completed ? "Goal reached!" : `${goal.progress} / ${goal.target} coins`}
        </span>
      </div>
      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
        <motion.div className="h-full gold-gradient" initial={false} animate={{ width: `${goal.completed ? 100 : pct}%` }} transition={{ type: "spring", stiffness: 80, damping: 18 }} />
      </div>
      {goal.leaders?.length > 0 && (
        <div data-testid={`${testid}-leaders`} className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-[#E0E0E0]">
          {goal.leaders.map((l, i) => (
            <span key={l.id} className="flex items-center gap-1">
              {i === 0 ? <Crown size={12} className="text-[#D4AF37]" /> : <span className="text-[#D4AF37]">{i + 1}.</span>}
              <span className="truncate max-w-[90px]">{l.name}</span> <span className="text-[#D4AF37]">{l.coins}</span>
            </span>
          ))}
        </div>
      )}
      {!compact && goal.on_complete === "restart" && (
        <div className="text-[10px] text-[#A0A0A0] mt-1.5 flex items-center gap-1"><Repeat size={10} /> Repeats when reached · completed {goal.completions || 0}×</div>
      )}
    </div>
  );
};

const PARTICLES = Array.from({ length: 28 }, (_, i) => ({ i, x: Math.cos(i * 0.9) * (120 + (i % 5) * 40), y: Math.sin(i * 0.9) * (120 + (i % 4) * 45) }));

export const GoalCelebration = ({ celebrate }) => (
  <AnimatePresence>
    {celebrate && (
      <motion.div key={celebrate.key} data-testid="goal-celebration" className="fixed inset-0 z-[70] pointer-events-none flex items-center justify-center"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        {PARTICLES.map((p) => (
          <motion.span key={p.i} className="absolute w-2.5 h-2.5 rounded-sm gold-gradient"
            initial={{ x: 0, y: 0, scale: 0, rotate: 0 }} animate={{ x: p.x, y: p.y, scale: [0, 1.2, 0.8], rotate: 360, opacity: [1, 1, 0] }}
            transition={{ duration: 1.8, ease: "easeOut" }} />
        ))}
        <motion.div initial={{ scale: 0.6, y: 20 }} animate={{ scale: 1, y: 0 }} className="pe-card rounded-3xl px-8 py-6 text-center border border-[#D4AF37]/60">
          <Trophy className="text-[#D4AF37] mx-auto mb-2" size={36} />
          <div className="font-heading text-3xl gold-text">Goal reached!</div>
          <div className="text-sm text-white mt-1">{celebrate.name ? `${celebrate.name}: ` : ""}{celebrate.title}</div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export const TipGoalEditor = ({ goal, onSaved }) => {
  const [f, setF] = useState({ title: "", target: 200, on_complete: "stop" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.title.trim()) { toast.error("Give your goal a name"); return; }
    const target = Number(f.target);
    if (!Number.isInteger(target) || target < 10 || target > 100000) { toast.error("Set a target between 10 and 100,000 coins"); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/creator/tip-goal", { ...f, target: Number(f.target) });
      toast.success("Tip goal is live");
      setF({ title: "", target: 200, on_complete: "stop" });
      onSaved?.(data.goal);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save goal"); } finally { setBusy(false); }
  };
  const clear = async () => {
    try { await api.delete("/creator/tip-goal"); toast.success("Goal removed"); onSaved?.(null); }
    catch (e) { toast.error("Couldn't remove goal"); }
  };
  const modeBtn = (val, label) => (
    <button type="button" data-testid={`goal-mode-${val}`} onClick={() => setF({ ...f, on_complete: val })}
      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${f.on_complete === val ? "gold-gradient text-black font-medium border-transparent" : "border-[#2A2A2A] text-[#A0A0A0] hover:text-white"}`}>{label}</button>
  );
  return (
    <div data-testid="tip-goal-editor" className="space-y-3">
      {goal && (
        <div className="space-y-2">
          <TipGoalBar goal={goal} testid="my-tip-goal" />
          <button data-testid="goal-clear" onClick={clear} className="text-xs text-[#A0A0A0] hover:text-[#E63946]">Remove current goal</button>
        </div>
      )}
      <div className="text-xs text-[#A0A0A0]">{goal ? "Set your next goal (this replaces the current one):" : "Fans see a live progress bar. Gifts and coin tips count toward it."}</div>
      <input data-testid="goal-title-input" maxLength={60} placeholder="e.g. Unlock the masquerade dance" value={f.title}
        onChange={(e) => setF({ ...f, title: e.target.value })} className="pe-input w-full rounded-full px-4 py-2 text-sm" />
      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="goal-target-input" type="number" min={10} max={100000} value={f.target}
          onChange={(e) => setF({ ...f, target: e.target.value })} className="pe-input rounded-full px-4 py-2 text-sm w-28" />
        <span className="text-xs text-[#A0A0A0]">coins · when reached:</span>
        {modeBtn("stop", "Celebrate & stop")}
        {modeBtn("restart", "Celebrate & restart")}
      </div>
      <button data-testid="goal-save" onClick={save} disabled={busy} className="btn-gold px-5 py-2 rounded-full text-sm flex items-center gap-2 disabled:opacity-50">
        {busy && <Loader2 size={14} className="animate-spin" />} {goal ? "Start new goal" : "Start goal"}
      </button>
    </div>
  );
};

export const useCelebration = () => {
  const [celebrate, setCelebrate] = useState(null);
  const timer = useRef(null);
  const fire = useCallback((name, title) => {
    clearTimeout(timer.current);
    setCelebrate({ key: Date.now(), name, title });
    timer.current = setTimeout(() => setCelebrate(null), 3500);
  }, []);
  return [celebrate, fire];
};
